import type { Catalog } from "./catalog.ts";
import { isSupportedLocale, type Locale } from "./locales.ts";

/** All namespaces of one locale, keyed by namespace — the shape `use-intl`/`next-intl` take as `messages`. */
export type Messages = Record<string, Catalog>;

async function loadEnglish(): Promise<Messages> {
  return (await import("../locales/en/index.ts")).default;
}

// English is the source: its index names the namespaces, so a new `<ns>.json` there is picked up for
// every translated locale without a second list to keep in step.
async function loadTranslated(locale: Exclude<Locale, "en">): Promise<Messages> {
  const namespaces = Object.keys(await loadEnglish());
  const entries = await Promise.all(
    namespaces.map(async (namespace) => {
      const catalog = await import(`../locales/${locale}/${namespace}.json`, { with: { type: "json" } });
      return [namespace, catalog.default as Catalog] as const;
    }),
  );
  return Object.fromEntries(entries);
}

// A `Record<Locale, …>` makes adding a locale to SUPPORTED_LOCALES a compile error until it has a loader here.
const loaders: Record<Locale, () => Promise<Messages>> = {
  en: loadEnglish,
  sv: () => loadTranslated("sv"),
};

export async function loadMessages(locale: Locale): Promise<Messages> {
  if (!isSupportedLocale(locale)) {
    throw new Error(`Unsupported locale "${String(locale)}"`);
  }
  return loaders[locale]();
}
